import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Button,
  Avatar,
  TableContainer,
  Paper,
} from '@mui/material';
import './UpcomingSessionTable.css';
import '../../SessionTable/SessionTable.css';
import OneOnOneSessionIcon from '../../../assets/Dashboard/oneoneonesession.svg';
import GroupSessionIcon from '../../../assets/Dashboard/group.svg';
import PrimeSessionIcon from '../../../assets/Dashboard/prime.svg';
import { useTranslation } from '../../../i18n/LanguageContext';
import { styled } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import usePagination from '@mui/material/usePagination';
import { useNavigate } from 'react-router-dom';

const SessionTypeColor = {
  one_on_one: {
    color: '#0A5EF3',
    bgcolor: '#E8F0FF',
    value: 'One-On-One',
    icon: OneOnOneSessionIcon,
  },
  group: {
    color: '#7BCF8C',
    bgcolor: '#FAFFFB',
    value: 'Group',
    icon: GroupSessionIcon,
  },
  prime: {
    color: '#AD8B00',
    bgcolor: '#F0EFFF',
    value: 'Prime',
    icon: PrimeSessionIcon,
  },
};
const List = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: '10px 0',
  display: 'flex',
  justifyContent: 'center',
});

const formatLevel = level => {
  if (!level) return 'N/A';
  return level.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

const UpcomingSessionTable = ({ sessions }) => {
  const navigate = useNavigate();
  // Pagination state
  const [page, setPage] = useState(1);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const rowsPerPage = 5;
  useEffect(() => setFilteredSessions(sessions), [sessions]);
  const { items } = usePagination({
    count: Math.ceil(filteredSessions.length / rowsPerPage),
    onChange: (e, v) => setPage(v),
  });
  const startIndex = (page - 1) * rowsPerPage;
  const currentRows = filteredSessions.slice(startIndex, startIndex + rowsPerPage);

  // Handle click on a table row
  const handleCellClick = (event, id) => {
    // Check if the click is from the actions column
    const isActionsCell = event.target.closest('td:last-child');
    if (!isActionsCell) {
      navigate(`/instructor/sessions-details/${id}`);
    }
  };

  return (
    <Box className="upcoming-sessions-container">
      {/* <Typography variant="h6" className="section-title">
        Upcoming Sessions
      </Typography> */}
      <TableContainer component={Paper} sx={{ marginTop: 2, overflowX: 'auto' }}>
        <Table sx={{ minWidth: 650 }} aria-label="upcoming sessions table">
          <TableHead
            sx={{
              backgroundColor: '#EEF4FF',
              '& th': {
                color: 'var(--Text, #14171F)',
                fontFamily: 'Poppins',
                fontSize: '12px',
                fontWeight: 600,
                lineHeight: 'normal',
                padding: '10px 16px',
                textTransform: 'uppercase',
              },
            }}
          >
            <TableRow>
              <TableCell>LEARNER NAME</TableCell>
              <TableCell>DATE</TableCell>
              <TableCell>TIMING</TableCell>
              <TableCell>SUBJECT</TableCell>
              <TableCell>SCHOOL LEVEL</TableCell>
              <TableCell>SESSIONS TYPE</TableCell>
              <TableCell>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& td': {
                color: 'var(--Text, #14171F)',
                fontFamily: 'Poppins',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: 'normal',
                padding: '9px 16px',
              },
              '& tr:hover': {
                backgroundColor: '#E9F1FF',
                cursor: 'pointer',
              },
            }}
          >
            {currentRows.length > 0 ? (
              currentRows.map(session => {
                // Determine type key for mapping
                const key = session.sessionType.toLowerCase().replace(/-/g, '_').replace(/ /g, '_');
                const typeInfo = SessionTypeColor[key] || {};
                return (
                  <TableRow
                    key={session.id}
                    className="session-row"
                    onClick={event => handleCellClick(event, session.id)}
                  >
                    <TableCell>
                      <Box className="student-cell">
                        <Avatar
                          src={session.avatarUrl}
                          alt={session.studentName}
                          className="student-avatar"
                        />
                        <Typography variant="body2" className="name-text">
                          {session.studentName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{session.date}</TableCell>
                    <TableCell>{session.timing}</TableCell>
                    <TableCell>{session.subject}</TableCell>
                    <TableCell>{formatLevel(session.level)}</TableCell>
                    <TableCell>
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          color: typeInfo.color,
                        }}
                      >
                        <img
                          src={typeInfo.icon}
                          alt={typeInfo.value}
                          style={{ width: 20, height: 20 }}
                        />
                        <Typography variant="body2" component="span" className="level-text">
                          {typeInfo.value || session.sessionType}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant={session.canJoin ? 'contained' : 'outlined'}
                        className={session.canJoin ? 'join-button active' : 'join-button disabled'}
                        size="small"
                        disabled={!session.canJoin}
                      >
                        Join
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ padding: '30px 16px' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    No upcoming sessions found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Only show pagination if there are sessions */}
      {currentRows.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <nav>
            <List>
              {items.map(({ page: itemPage, type, selected, ...item }, index) => {
                let children = null;
                if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                  children = '…';
                } else if (type === 'page') {
                  children = (
                    <button
                      type="button"
                      style={{
                        fontWeight: selected ? 'bold' : undefined,
                        margin: '0 5px',
                        padding: '5px 10px',
                        cursor: 'pointer',
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        color: selected ? '#000' : '#666',
                        backgroundColor: selected ? '#f0f0f0' : 'transparent',
                        border: 'none',
                        borderRadius: '4px',
                      }}
                      {...item}
                    >
                      {itemPage}
                    </button>
                  );
                } else {
                  children = (
                    <button
                      type="button"
                      style={{
                        margin: '0 5px',
                        padding: '5px 10px',
                        cursor: 'pointer',
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        color: '#666',
                        border: 'none',
                        backgroundColor: 'transparent',
                      }}
                      {...item}
                    >
                      {formatLevel(type)}
                    </button>
                  );
                }
                return <li key={index}>{children}</li>;
              })}
            </List>
          </nav>
        </Box>
      )}
    </Box>
  );
};

UpcomingSessionTable.propTypes = {
  sessions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      studentName: PropTypes.string.isRequired,
      avatarUrl: PropTypes.string,
      date: PropTypes.string.isRequired,
      timing: PropTypes.string.isRequired,
      subject: PropTypes.string.isRequired,
      level: PropTypes.string.isRequired,
      sessionType: PropTypes.string.isRequired,
      canJoin: PropTypes.bool.isRequired,
    })
  ),
};

UpcomingSessionTable.defaultProps = {
  sessions: [],
};

export default UpcomingSessionTable;
